// Resource Budget Settings Types

import type { RevisionReasonCategory } from './budget.types';

export type ResourceBudgetType = 'simple' | 'monthly' | 'none';

export interface RoleBudgetEntry {
  id: string;
  role: string;
  budgetHours: number;
  rate: number;
  estimatedCost: number;
}

export interface MonthlyAllocation {
  month: string; // Format: 'YYYY-MM'
  hours: number;
}

export interface MonthlyRoleBudget extends RoleBudgetEntry {
  monthlyAllocations: MonthlyAllocation[];
  allocatedHours: number; // Sum of monthly allocations
  isAllocated: boolean; // True if allocatedHours === budgetHours
}

export interface ResourceBudgetSettings {
  budgetType: ResourceBudgetType;
  
  // Simple budget
  roleBudgets?: RoleBudgetEntry[];
  
  // Monthly budget
  monthlyRoleBudgets?: MonthlyRoleBudget[];
  
  // Project timeline for monthly budgets
  projectStartDate?: string;
  projectEndDate?: string;
  
  // Metadata
  lastUpdated?: string;
  updatedBy?: string;
}

export interface ResourceBudgetSummary {
  totalHours: number;
  totalEstimatedCost: number;
  roleCount: number;
}

// Validation errors
export interface ValidationError {
  field: string;
  message: string;
}

// Auto-fill options for monthly allocation
export type AutoFillStrategy = 
  | 'even' 
  | 'front-loaded' 
  | 'back-loaded' 
  | 'custom';

export interface AutoFillOptions {
  strategy: AutoFillStrategy;
  frontLoadPercent?: number; // For front-loaded (e.g., 60% in first half)
  backLoadPercent?: number; // For back-loaded (e.g., 60% in second half)
}

// ============================================================================
// REVISION SETTINGS TYPES
// ============================================================================

/**
 * Per-organisation configuration for a single revision reason category.
 * Default categories are seeded by the system; custom categories are org-created.
 */
export interface RevisionReasonCategoryConfig {
  /** Stable ID — matches the `RevisionReasonCategory` union for default entries */
  id: RevisionReasonCategory | string;
  /** Human-readable label shown in dropdowns and reports */
  label: string;
  /** True for the 8 system-seeded categories */
  isDefault: boolean;
  /** Whether the category appears in the PM-facing dropdown */
  isEnabled: boolean;
  /** Controls sort order in the dropdown and settings manager */
  displayOrder: number;
  /** True for org-added categories — enables editing/deletion */
  isCustom: boolean;
}

/**
 * Org-level settings controlling Resource Budget revision behaviour.
 * Stored per-organisation; users override per-revision at save time.
 */
export interface BudgetRevisionSettings {
  /** Auto-recalculate Labour lines in Cost Budget when a Resource Budget revision is saved */
  autoUpdateCostLabour: boolean;
  /** Auto-recalculate Labour lines in Sales Budget when a Resource Budget revision is saved */
  autoUpdateSalesLabour: boolean;
  /**
   * When true, PMs must select a reason category and optionally add notes before saving.
   * When false (default), saves happen silently — notes are optional and can be added via the toast.
   * Notes are always required when making a revision active.
   */
  requireRevisionNotes: boolean;
  /**
   * When true, PMs must select a reason category before saving any cost budget change.
   * Notes are always required when making a cost budget revision active, regardless of this setting.
   * Default: false.
   */
  requireRevisionNotesOnCostSave: boolean;
  /**
   * When true, PMs must select a reason category before saving any sales budget change.
   * Notes are always required when making a sales budget revision active, regardless of this setting.
   * Default: false.
   */
  requireRevisionNotesOnSalesSave: boolean;
  /** Ordered list of reason categories configured for this organisation */
  revisionReasonCategories: RevisionReasonCategoryConfig[];
  /**
   * The ID of the GlobalCategory (type: 'labour') that the Resource Budget cascade targets.
   * When set, only this category receives cascaded hours-to-monetary values from the Resource Budget.
   * When null/undefined, falls back to updating all labour-type categories (legacy behaviour).
   */
  cascadeTargetCategoryId?: number | null;
}

/**
 * The 8 system-default reason categories shipped with the feature.
 * Organisations start with all defaults enabled; they can disable, reorder, and add custom entries.
 */
export const DEFAULT_REVISION_REASON_CATEGORIES: RevisionReasonCategoryConfig[] = [
  {
    id: 'scope-change',
    label: 'Scope Change',
    isDefault: true,
    isEnabled: true,
    displayOrder: 1,
    isCustom: false,
  },
  {
    id: 'rate-change',
    label: 'Rate Change',
    isDefault: true,
    isEnabled: true,
    displayOrder: 2,
    isCustom: false,
  },
  {
    id: 'resource-substitution',
    label: 'Resource Substitution',
    isDefault: true,
    isEnabled: true,
    displayOrder: 3,
    isCustom: false,
  },
  {
    id: 'timeline-extension',
    label: 'Timeline Extension',
    isDefault: true,
    isEnabled: true,
    displayOrder: 4,
    isCustom: false,
  },
  {
    id: 'initial-estimate',
    label: 'Initial Estimate',
    isDefault: true,
    isEnabled: true,
    displayOrder: 5,
    isCustom: false,
  },
  {
    id: 'client-request',
    label: 'Client Request',
    isDefault: true,
    isEnabled: true,
    displayOrder: 6,
    isCustom: false,
  },
  {
    id: 'internal-replan',
    label: 'Internal Replan',
    isDefault: true,
    isEnabled: true,
    displayOrder: 7,
    isCustom: false,
  },
  {
    id: 'contract-amendment',
    label: 'Contract Amendment',
    isDefault: true,
    isEnabled: true,
    displayOrder: 8,
    isCustom: false,
  },
];
